import type {ComputedRef, Ref} from 'vue';
import {computed, reactive, ref, watch} from 'vue';
import {useScreenSizeStore} from '@common/stores';
import debounce from 'lodash-es/debounce';
import {storeToRefs} from 'pinia';
import type {TypedAggregationResource} from './filters.types';

interface UseSearchFilterOptions<TType extends string> {
    aggregations: ComputedRef<TypedAggregationResource<TType>[]>;
    onSearch: (value: string) => Promise<TypedAggregationResource<TType>[] | undefined>;
}

export function useSearchFilter<TType extends string>({aggregations, onSearch}: UseSearchFilterOptions<TType>) {
    const screeSizeStore = useScreenSizeStore();
    const {isProjectListDesktopScreen} = storeToRefs(screeSizeStore);

    const searchTerm = ref('');
    const isSearchActive = ref(false);
    const resultsSize = ref(6);

    const DESKTOP_ELEMENTS = 4;
    const pending = ref(false);

    const safeAggregations: Ref<TypedAggregationResource<string>[]> = ref(aggregations.value ?? []);

    const computedResults = computed(() => (isSearchActive.value ? safeAggregations.value : aggregations.value));
    const hasMoreToShow = computed(
        () => safeAggregations.value.length > resultsSize.value + (isProjectListDesktopScreen.value ? DESKTOP_ELEMENTS : 0),
    );

    const displayedResults = computed(() => {
        return safeAggregations.value.slice(0, resultsSize.value);
    });

    const triggerSearch = debounce(handleSearch, 200);

    async function handleSearch(value: string) {
        try {
            pending.value = true;
            if (value.length >= 1) {
                isSearchActive.value = true;
            } else {
                isSearchActive.value = false;
            }
            const results = await onSearch(value);
            safeAggregations.value = results ?? [];
        } finally {
            pending.value = false;
        }
    }

    watch(searchTerm, triggerSearch);

    watch(
        aggregations,
        () => {
            if (!pending.value && !searchTerm.value) {
                safeAggregations.value = aggregations.value ?? [];
            }
        },
        {deep: true},
    );

    return reactive({
        searchTerm,
        resultsSize,
        safeAggregations,
        computedResults,
        hasMoreToShow,
        displayedResults,
        isSearchActive,
        pending,
    });
}
